import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, RefreshCw, GraduationCap } from 'lucide-react';
import { authFetch } from "./api";

const API = 'https://hs.aida-advisory.com';

const POSITIONS = ['All', 'QB', 'RB', 'FB', 'WR', 'TE', 'OT', 'OG', 'C', 'RE', 'PE', 'DT', 'SAM', 'MIKE', 'WILL', 'CB', 'S', 'K', 'P', 'LS', 'KR', 'PR'];
const CLASS_YEARS = ['All', 'Senior', 'Junior', 'Sophomore', 'Freshman'];

const gradeColor = (g) => {
  if (!g) return '#546E7A';
  if (g >= 8) return '#00E676';
  if (g >= 7) return '#00C853';
  if (g >= 6) return '#FFD600';
  if (g >= 4) return '#FFB300';
  return '#EF5350';
};

const gpaColor = (gpa) => {
  if (!gpa) return '#546E7A';
  if (gpa >= 3.5) return '#00E676';
  if (gpa >= 2.5) return '#FFD600';
  return '#EF5350';
};

const tierBadgeClass = (tier) => {
  switch (tier) {
    case 'ELITE FIT':    return 'bg-[#00E676]/10 text-[#00E676] border-[#00E676]/30';
    case 'STRONG FIT':   return 'bg-[#00C853]/10 text-[#00C853] border-[#00C853]/30';
    case 'GOOD FIT':     return 'bg-[#FFD600]/10 text-[#FFD600] border-[#FFD600]/30';
    case 'MARGINAL FIT': return 'bg-[#FFB300]/10 text-[#FFB300] border-[#FFB300]/30';
    default:             return 'bg-[#EF5350]/10 text-[#EF5350] border-[#EF5350]/30';
  }
};

const eligibilityDot = (status) => {
  if (status === 'eligible')   return 'bg-[#00E676]';
  if (status === 'at_risk')    return 'bg-[#FFB300]';
  if (status === 'ineligible') return 'bg-[#EF5350]';
  return 'bg-[#546E7A]';
};

const RecruitBoard = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classYear, setClassYear] = useState('All');
  const [position, setPosition] = useState('All');
  const [sortField, setSortField] = useState('fit_score');
  const [sortDir, setSortDir] = useState('desc');

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/players/draft-board?include_ungraded=true&`;
      if (position !== 'All') url += `position=${position}&`;
      const res = await authFetch(url);
      if (res.ok) setPlayers(await res.json());
    } catch (err) {
      console.error('Error fetching recruit board:', err);
    } finally {
      setLoading(false);
    }
  }, [position]);

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'fit_score' ? 'desc' : 'asc');
    }
  };

  const filtered = players.filter(p =>
    classYear === 'All' || p.class_year === classYear
  );

  const sortedAll = [...filtered].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Ungraded prospects have no fit score, so they cannot be ranked. They still
  // need to be visible -- a player who has been added but not evaluated was
  // previously findable only by searching their name.
  const sorted = sortedAll.filter((p) => p.fit_score != null);
  const ungraded = sortedAll.filter((p) => p.fit_score == null);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp size={12} className="text-[#546E7A]" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-[#00D4FF]" />
      : <ChevronDown size={12} className="text-[#00D4FF]" />;
  };

  const ColHeader = ({ field, label }) => (
    <th
      className="text-left py-3 px-4 text-[#B0BEC5] font-semibold text-sm cursor-pointer hover:text-white select-none"
      onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1">{label} <SortIcon field={field} /></span>
    </th>
  );

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6">
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-12">

        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-5 md:mb-6">
          <div>
            <h1 className="text-white text-2xl font-bold">Recruit Board</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">
              {sorted.length} ranked by organizational fit
              {ungraded.length > 0 && ` · ${ungraded.length} not yet graded`}
            </p>
          </div>
          <button onClick={fetchPlayers}
            className="flex items-center gap-2 px-3 py-2 border border-[#546E7A]/40 text-[#546E7A] hover:text-white hover:border-[#00D4FF]/50 rounded-lg text-sm transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="panel-card mb-6">
          <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-4 md:gap-6">

            {/* Class Year Filter */}
            <div>
              <div className="text-[#546E7A] text-xs uppercase tracking-wider mb-2">Class Year</div>
              <div className="chip-row md:flex md:gap-2">
                {CLASS_YEARS.map(cy => (
                  <button key={cy} onClick={() => setClassYear(cy)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      classYear === cy
                        ? 'bg-[#00D4FF]/20 border-[#00D4FF] text-[#00D4FF]'
                        : 'bg-[#243B55]/50 border-[#546E7A]/30 text-[#B0BEC5] hover:border-[#00D4FF]/50 hover:text-white'
                    }`}>
                    {cy}
                  </button>
                ))}
              </div>
            </div>

            {/* Position Filter */}
            <div className="md:flex-1 min-w-0">
              <div className="text-[#546E7A] text-xs uppercase tracking-wider mb-2">Position</div>
              <div className="chip-row md:flex md:flex-wrap md:gap-1.5">
                {POSITIONS.map(pos => (
                  <button key={pos} onClick={() => setPosition(pos)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                      position === pos
                        ? 'bg-[#00D4FF]/20 border-[#00D4FF] text-[#00D4FF]'
                        : 'bg-[#243B55]/50 border-[#546E7A]/30 text-[#B0BEC5] hover:border-[#00D4FF]/50 hover:text-white'
                    }`}>
                    {pos}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="text-[#00D4FF]">Loading recruit board...</div>
          </div>
        )}

        {/* Empty */}
        {!loading && sorted.length === 0 && ungraded.length === 0 && (
          <div className="panel-card text-center py-16">
            <div className="text-white text-lg mb-2">No prospects on the board yet</div>
            <div className="text-[#B0BEC5] text-sm mb-4">Prospects appear here once they have been graded.</div>
            <button onClick={() => navigate('/players')} className="btn-primary">Go to Players</button>
          </div>
        )}

        {/* Board Table */}
        {!loading && sorted.length > 0 && (
          <div className="panel-card p-0 overflow-hidden bg-transparent lg:bg-[#1A2744] border-0 lg:border">
            <table className="w-full wide-only">
              <thead>
                <tr className="border-b border-[#243B55]">
                  <th className="text-left py-3 px-4 text-[#B0BEC5] font-semibold text-sm w-10">#</th>
                  <ColHeader field="name" label="Prospect" />
                  <ColHeader field="position" label="Pos" />
                  <ColHeader field="class_year" label="Class" />
                  <th className="text-left py-3 px-4 text-[#B0BEC5] font-semibold text-sm">High School</th>
                  <ColHeader field="gpa" label="GPA" />
                  <th className="text-left py-3 px-4 text-[#B0BEC5] font-semibold text-sm">
                    <span className="flex items-center gap-1">
                      <GraduationCap size={12} /> Eligibility
                    </span>
                  </th>
                  <ColHeader field="fit_score" label="Fit Score" />
                  <th className="text-left py-3 px-4 text-[#B0BEC5] font-semibold text-sm">Tier</th>
                  <th className="text-left py-3 px-4 text-[#B0BEC5] font-semibold text-sm">Grade %</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((player, index) => (
                  <tr
                    key={player.id}
                    onClick={() => navigate(`/players/${player.id}/fit`)}
                    className="border-b border-[#243B55]/50 last:border-0 hover:bg-[#243B55]/40 cursor-pointer transition-colors group">

                    <td className="py-3 px-4 text-[#546E7A] text-sm font-rajdhani">{index + 1}</td>

                    <td className="py-3 px-4">
                      <span className="text-white text-sm font-medium group-hover:text-[#00D4FF] transition-colors">
                        {player.name}
                      </span>
                    </td>

                    <td className="py-3 px-4">
                      <span className="text-[#B0BEC5] text-sm">{player.position}</span>
                    </td>

                    <td className="py-3 px-4">
                      <span className="text-[#B0BEC5] text-sm">{player.class_year || '—'}</span>
                    </td>

                    <td className="py-3 px-4">
                      <span className="text-[#B0BEC5] text-sm">{player.high_school || '—'}</span>
                    </td>

                    {/* GPA */}
                    <td className="py-3 px-4">
                      <span className="font-rajdhani font-bold text-base"
                        style={{ color: gpaColor(player.gpa) }}>
                        {player.gpa ? player.gpa.toFixed(2) : '—'}
                      </span>
                    </td>

                    {/* Eligibility dot */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${eligibilityDot(player.ncaa_eligibility)}`} />
                        <span className="text-[#B0BEC5] text-xs capitalize">
                          {player.ncaa_eligibility === 'at_risk' ? 'At Risk' :
                           player.ncaa_eligibility === 'eligible' ? 'Eligible' :
                           player.ncaa_eligibility === 'ineligible' ? 'Ineligible' : 'Unknown'}
                        </span>
                      </div>
                    </td>

                    {/* Fit Score */}
                    <td className="py-3 px-4">
                      <span className="font-rajdhani font-bold text-xl"
                        style={{ color: gradeColor(player.fit_score) }}>
                        {player.fit_score.toFixed(1)}
                      </span>
                    </td>

                    {/* Tier */}
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${tierBadgeClass(player.tier)}`}>
                        {player.tier}
                      </span>
                    </td>

                    {/* Confidence */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-[#243B55] rounded-full overflow-hidden">
                          <div className="h-full bg-[#00D4FF] rounded-full"
                            style={{ width: `${Math.round(player.confidence_score * 100)}%` }} />
                        </div>
                        <span className="text-[#546E7A] text-xs">
                          {Math.round(player.confidence_score * 100)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile: a ranked card per prospect. Ten columns will not fit a phone. */}
            <div className="narrow-only space-y-2.5">
              {sorted.map((player, index) => (
                <div
                  key={player.id}
                  onClick={() => navigate(`/players/${player.id}/fit`)}
                  className="list-card flex items-center gap-3"
                >
                  <div className="font-rajdhani font-bold text-lg text-[#546E7A] w-6 flex-shrink-0 text-center">
                    {index + 1}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-white font-semibold truncate">{player.name}</div>
                    <div className="text-[#B0BEC5] text-xs mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[#00D4FF] font-semibold">{player.position}</span>
                      {player.class_year && <span>• {player.class_year}</span>}
                      {player.high_school && <span className="truncate">• {player.high_school}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${tierBadgeClass(player.tier)}`}>
                        {player.tier}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className={`w-2 h-2 rounded-full ${eligibilityDot(player.ncaa_eligibility)}`} />
                        <span className="text-[#546E7A] text-[10px]">
                          GPA {player.gpa ? player.gpa.toFixed(2) : '—'}
                        </span>
                      </span>
                      <span className="text-[#546E7A] text-[10px]">
                        {Math.round(player.confidence_score * 100)}% conf.
                      </span>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="font-rajdhani font-bold text-2xl leading-none"
                      style={{ color: gradeColor(player.fit_score) }}>
                      {player.fit_score.toFixed(1)}
                    </div>
                    <div className="text-[#546E7A] text-[9px] uppercase tracking-wide mt-1">Fit</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Not yet graded */}
        {!loading && ungraded.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-[#B0BEC5] font-bold text-sm">
                Not yet graded ({ungraded.length})
              </h2>
              <span className="text-[#546E7A] text-xs">
                — grade them to place them on the board
              </span>
            </div>
            <div className="space-y-2">
              {ungraded.map((player) => (
                <div
                  key={player.id}
                  onClick={() => navigate(`/players/${player.id}/fit?tab=grading`)}
                  className="list-card flex items-center gap-3 cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-semibold truncate">{player.name}</div>
                    <div className="text-[#B0BEC5] text-xs mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[#00D4FF] font-semibold">{player.position}</span>
                      {player.class_year && <span>• {player.class_year}</span>}
                      {player.high_school && <span className="truncate">• {player.high_school}</span>}
                      {player.gpa && <span>• {Number(player.gpa).toFixed(2)} GPA</span>}
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-[#546E7A] border border-[#546E7A]/40 rounded px-2 py-1 flex-shrink-0">
                    GRADE
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default RecruitBoard;
